import { Button, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { BookUser } from 'lucide-react';

/**
 * Empty-state panel for the clients list. Two cases, and they are different
 * screens, not one screen with a swapped noun:
 *
 *  - Nothing imported yet. A contractor's clients are not missing — they are
 *    already written on every estimate and invoice already sent. So the copy
 *    says that, and the SCAN is the primary action, not "New client" (which
 *    still sits in the page header where it always is). Fastest path to value
 *    wins the button.
 *  - A search matched nothing. The only useful action is getting back out of
 *    the search, so that is the action offered. It used to be a dead end.
 *
 * Composition is left-aligned inside the panel and reads top-to-bottom: the
 * bare `BookUser` mark, the state, one line of why, the way forward. Not the
 * centred icon-heading-body-button stack, which is a preset. The mark stays
 * bare — no tile, no chip, no circle behind it.
 */
export function ClientsEmptyState({
  searched,
  onImport,
  importing = false,
}: {
  searched: boolean;
  /** Runs the document scan. Unused in the searched case. */
  onImport?: () => void;
  importing?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <section className="max-w-2xl rounded-md border border-border bg-card px-6 py-12 sm:px-10">
      <div>
        <BookUser className="size-8 text-muted-foreground" aria-hidden="true" />
        <h2 className="mt-5 font-display font-semibold text-foreground text-xl">
          {searched ? t('clients:empty.searchHeading') : t('clients:empty.heading')}
        </h2>
        <p className="mt-2 text-muted-foreground text-sm">
          {searched ? t('clients:empty.searchBody') : t('clients:empty.body')}
        </p>
        <div className="mt-6">
          {searched ? (
            <Button asChild variant="secondary">
              <Link to="/clients" search={{}}>
                {t('clients:empty.clearSearch')}
              </Link>
            </Button>
          ) : (
            <Button type="button" onClick={onImport} disabled={importing || !onImport}>
              {importing ? t('clients:actions.importing') : t('clients:actions.import')}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
